<template>
  <div class="payment-invoices-wrap">

    <portal to="dash-modal">
      <InvoicePaymentsModal
        v-if="showPaymentsModal && newPayment"
        v-model="newPayment"
        @add-payment="addPayment"
        @close="showPaymentsModal = false"
      />
      <InvoiceUpdateEURModal
        v-if="showConvertModal && invoiceAmount"
        v-model="invoiceAmount"
        :errors="newPayment.errors"
        @update-euro-total="updateEuroTotal"
        @close="showConvertModal = false"
      />
    </portal>

    <div class="invoice-wrap-bg">
      <div class="invoice-wrap" style="gap: 1rem;">

        <div class="invoice-section">
          <div class="" style="display: flex; gap: 1rem; flex-direction: column;">

            <div style="display: flex; flex-wrap: wrap; gap: 1rem;">
              <!-- Totals -->
              <div style="flex: 1;">

                <div v-if="!isEUR" class="d-flex align-items-center" style="gap: 1rem;">
                  <div class="d-flex align-items-center" style="gap: 1rem;">
                    <h4 class="text-nowrap">{{ $gettext('Totals') }}<span> ({{ $gettext('in €') }})</span></h4>
                    <GekkoButton
                      @click="showConvertModal = true"
                      :icon="'fa-regular fa-pen-to-square'"
                      :text="$gettext('Update EUR amount')"
                      variant="link" size="sm" class="p-0"
                    />
                  </div>
                </div>

                <div class="d-flex" style="gap: 2rem;">
                  <div>
                    <div class="gk-modal-label text-nowrap">{{ $gettext('Invoice total') }}</div>
                    <span style="font-size: 20px;">{{ invoice.total_amount | currency('EUR', 2) }}</span>
                  </div>
                  <div>
                    <div class="gk-modal-label text-nowrap">{{ $gettext('Amount due') }}</div>
                    <span style="font-size: 20px;">{{ invoice.total_amount - totalPaid | currency('EUR', 2) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="!isEUR" style="flex: 1;">
                <div class="d-flex align-items-center" style="gap: 1rem;">
                  <h4 class="text-nowrap">{{ $gettext('Totals') }}<span> (in {{ invoice.currency }})</span></h4>
                </div>
                <div class="d-flex" style="gap: 2rem;">
                  <div>
                    <div class="gk-modal-label text-nowrap">{{ $gettext('Invoice total') }}</div>
                    {{ invoice.currency_total_amount | currency(invoice.currency, 2) }}
                  </div>
                </div>
              </div>
            </div>

            
          </div> <!-- is-shadow-box -->
        </div> <!-- invoice-section -->

        <div v-if="!isPaid && !isEUR && !isPaymentAllowed" class="d-flex">
          <b-alert show class="gk-alert mb-0" variant="warning">
            <div @click.prevent="showConvertModal = true" v-html="convertAmountText" />
          </b-alert>
        </div>
        <div v-if="isOverPaid" class="d-flex">
          <b-alert show class="gk-alert mb-0" variant="warning">
            <div>
              {{ $gettext('This invoice seems to be overpaid. Please check payment amounts.') }}
            </div>
          </b-alert>
        </div>

        <div class="invoice-section">
          <div class="invoice-section--label" style="display: flex; justify-content: space-between; align-items: flex-end;">
            <span>{{ $gettext('Payments') }}<span v-if="!isEUR"> (in &euro;)</span></span>
          </div>
          <div class="is-shadow-box" style="overflow: hidden; overflow-x: auto;">
            
            <!-- Payments table -->
            <b-table 
              :key="tableKey"
              :items="payments"
              :fields="paymentFields"
              class="invoice-payments-table"
              :class="['gk-table gk-table--responsive gk-table--headed gk-table--striped']"
              show-empty
            >
              <template #empty="">
                {{ $gettext('No payments for this invoice')}}
              </template>
              <template #cell(payment_date)="row">
                <span class="text-nowrap">
                  {{ row.item.payment_date | formatDate('DD-MM-YYYY') }}
                </span>
              </template>
              <template #cell(amount)="row">
                <span class="text-nowrap">
                  {{ row.item.amount | currency('EUR', 2) }}
                </span>
              </template>
              <template #cell(payment_type)="row">
                <div v-if="row.item.payment_type === 'bank'">
                  <div v-if="row.item.transaction" class="d-inline-block">
                    <font-awesome-icon icon="fa-solid fa-university" :id="'tt-connected-transaction' + row.item.id" style="width: 30px;" />
                    <b-tooltip :target="'tt-connected-transaction' + row.item.id" boundary="window" placement="top" triggers="hover">
                      <div class="tooltip-text">
                        {{ $gettext('Connected to a bank transaction') }}
                      </div>
                    </b-tooltip>
                  </div>
                  <div v-else class="d-inline-block">
                    <font-awesome-icon icon="fa-solid fa-building-columns" style="width: 30px;" />
                  </div>
                  {{ row.item.get_payment_type_display }}
                  <span v-if="row.item.sub_status === 'mollie'">{{ $gettext('via Mollie') }}</span>
                  <span v-if="row.item.sub_status === 'woocommerce'">{{ $gettext('via Woocommerce') }}</span>
                </div>

                <div v-if="row.item.payment_type === 'cash'">
                  <font-awesome-icon icon="fa-solid fa-sack-dollar" style="width: 30px;" />
                  {{ row.item.get_payment_type_display }}
                </div>
              </template>
              <template #cell(description)="row">
                {{ row.item.description }}
              </template>
              <template #cell(actions)="row">
                <GekkoButton 
                  icon="fa-solid fa-trash-can"
                  class="text-danger p-0"
                  variant="link"
                  v-gk-confirm="{
                    title: $gettext('Delete payment'),
                    message: $gettext('Are you sure you want to delete this payment?'),
                    options: {
                      variant: 'danger',
                      confirmText: $gettext('Delete')
                    },
                    callback: deletePayment,
                    callbackParams: row.item.id,
                  }"
                />
              </template>
            </b-table>
            <div style="display: flex; gap: 2rem; padding: 1rem;">
              <div>
                {{ $gettext('Total payments') }} <span style="font-size: 18px;">{{ totalPaid | currency('EUR', 2) }}</span>
              </div>
              <div v-if="isPaid" class="text-brand" style="display: flex; align-items: center;">
                <font-awesome-icon icon="fa-regular fa-circle-check" class="mr-1" />
                <span>{{ $gettext('Invoice fully paid') }}</span>
              </div>
            </div>
          </div> <!-- is-shadow-box -->
        </div> <!-- invoice-section -->

      </div> <!-- invoice-wrap -->
    </div> <!-- invoice-wrap-bg -->

  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";

import InvoiceService from "@/store/services/invoice.service.js"

import InvoicePaymentsModal from "@/components/invoices/ui/InvoicePaymentsModal"
import InvoiceUpdateEURModal from "@/components/invoices/ui/InvoiceUpdateEURModal"
import PaymentTypeSelector from "@/components/costs/PaymentTypeSelector"
import GekkoButton from "@/components/GekkoButton"

export default {
  name: 'InvoicePayments',
  components: { 
    GekkoButton,
    PaymentTypeSelector,
    InvoicePaymentsModal,
    InvoiceUpdateEURModal
  },
  props: {

  },
  data () {
    return {
      defaultPayment: {},
      newPayment: {},
      paymentTypeOptions: [
        { value: '', text: this.$gettext('Select a payment type') },
        { value: 'cash', text: this.$gettext('Cash or private account') },
        { value: 'bank', text: this.$gettext('Bank transaction') }
      ],
      paymentFields: [
        { key: "payment_date", label: this.$gettext('Date'), class: '' },
        { key: "amount", label: this.$gettext('Amount'), class: 'text-right' },
        { key: "payment_type", label: this.$gettext('Paid with'), class: '' },
        { key: "description", label: this.$gettext('Description'), class: '' },
        { key: "actions", label: '', class: '' },
      ],
      tableKey: 0,
      invoiceAmount: 0,
      exchangeRate: 0,
      showPaymentsModal: false,
      showConvertModal: false
    }
  },
  computed: {
    ...mapGetters({ 
      invoice: "invoices/getInvoice",
      payments: "invoices/getInvoicePayments",
    }),
    totalPaid () {
      let total = 0
      for (let index = 0; index < this.payments.length; index++) {
        total += parseFloat(this.payments[index].amount)
      }
      total = Math.trunc(total * 100) / 100;
      return total
    },
    isEUR () {
      return (this.invoice.currency === 'EUR')
    },
    isPaid () {
      return (this.invoice.total_amount > 0 && this.invoice.total_amount == this.totalPaid) || (this.invoice.total_amount == 0 && this.invoice.status === 'p')
    },
    isOverPaid () {
      return this.invoice.total_amount > 0 && this.invoice.total_amount < this.totalPaid
    },
    isPaymentAllowed () {
      if(this.invoice.total_amount < 0){
        return this.isCredited || this.invoice.total_amount < this.totalPaid
      }
      // loose comparison '==' for invoice.total_amount needed
      return this.isCredited || this.invoice.total_amount > this.totalPaid || (this.invoice.total_amount == 0 && this.invoice.status !== 'p')
    },
    isCredited () {
      return this.invoice.credited_invoice
    },
    convertAmountText () {
      return this.$gettext('To add payments you need to <span class=\'convert-amount-link\'>convert invoice amount to EUR</span>.')
    }
  },
  watch: {
    invoice () {
      this._update_total()
    },
    newPayment: {
      handler () {
        let totalToBePaid = parseFloat(this.newPayment.amount) + this.totalPaid
        totalToBePaid = parseFloat(totalToBePaid.toFixed(2))
        const invoiceAmount = parseFloat(this.invoice.total_amount)
        if (this.invoice.total_amount > 0 &&  totalToBePaid > invoiceAmount) {
          this.$set(this.newPayment.errors, 'paymentAmount', this.$gettext('Total payments would exceed the invoice amount.'))
        } else if (this.invoice.total_amount < 0 && totalToBePaid < invoiceAmount) {
          this.$set(this.newPayment.errors, 'paymentAmount', this.$gettext('Total payments would exceed the invoice amount.'))
        } else {
          delete this.newPayment.errors.paymentAmount
        }
        if (this.newPayment.payment_type && this.newPayment.errors) {
          delete this.newPayment.errors.payment_type
        }
      },
      deep: true
    }

  },
  created () {
    this.setupData()
    this.$emit('display-buttons', 'payments')
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    ...mapMutations({ 
      setInvoiceTotalAmount: 'invoices/SET_INVOICE_TOTAL_AMOUNT',
    }),
    ...mapActions({ 
      deleteStorePayment: 'invoices/deletePayment',
      addStorePayment: 'invoices/addPayment',
    }),
    close () {
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    setupData () {
      this.invoiceAmount = this.invoice.total_amount

      this.defaultPayment = {
        invoice: this.invoice.id,
        amount: 0,
        payment_type: '',
        description: '',
        payment_date: new Date().toString("yyyy-MM-dd"),
        errors: {}
      }

      this.newPayment = Object.assign({}, this.defaultPayment)
      this._update_total()
    },
    openPaymentsModal () {
      this.showPaymentsModal = true
    },
    deletePayment (payment_id) {
      this.deleteStorePayment(payment_id).then(()=> {
        this.tableKey++
        this._update_total()
        this._invoiceAmountIsValid()

        this.$emit('invoice-updated')
      })
    },
    addPayment() {
      if (!this.newPayment.amount) {
        this.$set(this.newPayment, 'amount', 0)
      }
      if ((typeof this.newPayment.amount) == 'string') {
        this.$set(this.newPayment, 'amount', parseFloat(this.newPayment.amount.replace(',', '.')))
      }
      if (!this.newPayment.payment_type) {
        this.$set(this.newPayment.errors, 'payment_type', this.$gettext('Please select a payment type.'))
      } else {
        if (this._isValidNewPayment()) {
          this.addStorePayment(this.newPayment).then(()=> {
            this._update_total()
            this.newPayment = Object.assign({}, this.defaultPayment)
            this.$emit('invoice-updated')
          }).catch((response) => {
            this.$set(this.newPayment, 'errors', response.data)
          })
        }
      }
    },
    _isValidNewPayment() {
      return (!this.newPayment.errors.paymentAmount && !this.newPayment.errors.payment_type)
    },
    updateEuroTotal () {
      if (this._invoiceAmountIsValid()) {
        InvoiceService.convertTotal(this.invoice.id, this.invoiceAmount).then(() => {
          this.$emit('invoice-updated')
          this.setInvoiceTotalAmount(this.invoiceAmount)
          this._update_total()
        })
      } else {
        console.log(this.newPayment.errors.invoiceAmount)
      }
    },

    _update_total () {
      this.newPayment.amount = Math.round((this.invoice.total_amount - this.totalPaid) * 100) / 100
      this.defaultPayment.amount = parseFloat(this.newPayment.amount).toFixed(2)
    },
    _invoiceAmountIsValid () {
      if ((this.invoiceAmount > 0 && this.invoiceAmount < this.totalPaid) || (this.invoiceAmount < 0 && this.invoiceAmount > this.totalPaid)) {
        this.$set(this.newPayment.errors, 'invoiceAmount', this.$gettext("Payments for this invoice exceed the new total. Please delete one or more payments first."))
        return false;
      } else if (!this.invoiceAmount) {
        this.$set(this.newPayment.errors, 'invoiceAmount', this.$gettext("This field is required."))
        return false;
      } else {
        delete this.newPayment.errors.invoiceAmount;
      }
      return true;
    }
  }
}
</script>

<style lang="scss">
  .gk-modal-payment-invoice {
    .gk-modal {
      max-width: 1200px;
      .action-buttons-wrapper {
        display: flex;
        gap: 0.75rem;
        justify-content: flex-end;
      }
    }
  }
  .payment-invoices-wrap {
    .convert-amount-link {
      color: $colorBlue;
      text-decoration: underline;
      cursor: pointer;
    }
  }
</style>
<style lang="scss" scoped>
  .payment-invoices-wrap {
    display: flex;
    flex-direction: column;
    @media (min-width: '768px') {
      height: 100%;
    }
  }
</style>
